import {Component, OnInit} from '@angular/core';
import {ConfigService as AnalyticsConfigService} from '@wdpr/angular-analytics';
import {AnalyticsHandler} from '../../shared/libraries/analytics-handler';
import {ApiService} from '../../shared/services/api.service';
import {ConfigService} from '../../shared/services/config.service';
import {TranslatePipe} from '../../shared/pipes/translate.pipe';
import {DateHandler} from '../../shared/libraries/date-handler';
import {Constants} from '../../shared/constants';
import {Utils} from '../../shared/libraries/utils';
import values from 'lodash-es/values';
import includes from 'lodash-es/includes';

@Component({
    selector: 'app-availability-calendar',
    templateUrl: './availability-calendar.component.html',
    styleUrls: ['./availability-calendar.component.scss'],
    providers: [TranslatePipe]
})
export class AvailabilityCalendarComponent implements OnInit {
    /**
     * Available dates
     * @var {Array<any>}
     */
    public availableDates: Array<any> = [];

    /**
     * Availability types (full, partial, none)
     * @var {object}
     */
    public availabilityTypes: object = {};

    /**
     * Current date range for API calls
     * @var {any}
     */
    public currentDateRange: any = {};

    /**
     * Selected date
     * @var {any}
     */
    public selectedDate: any = {};

    /**
     * Selected availability segment
     * @var {string}
     */
    public selectedSegment: string = '';

    /**
     * Available reservations title
     * @var {string}
     */
    public reservationsTitle: string = '';

    /**
     * Reservations footer disclaimer
     * @var {string}
     */
    public reservationsDisclaimer: string = '';

    /**
     * List of all parks
     * @var {Array<any>}
     */
    public parksList: Array<any> = [];

    /**
     * Calendar end date
     * @var {string}
     */
    public calendarEndDate: string;

    /**
     * Property to show or hide the Availability Information Component
     * @var {boolean}
     */
    public calendarHasContent: boolean = true;

    /**
     * Application configuration
     * @var {any}
     */
    private _config: any = {};

    /**
     * Current date
     * @var {string}
     */
    private _today: string;

    /**
     * Availability restrictions (max date the calendar will show)
     * @var {any}
     */
    private _availabilityRestrictions: any = {};

    /**
     * Debounce timeout for calendar requests
     * @var {any}
     */
    private _calendarDebounceTimeout: any;

    /**
     * Flag to check if page load analytics event was fired
     * @var {boolean}
     */
    private _pageLoadAnalyticsTriggered: boolean = false;

    /**
     * Error message
     * @var {string}
     */
    public errorMessage: string = null;

    /**
     * Default date selected
     * @var {string}
     */
    private _defaultDate: string;

    /**
     * Flag to check if the default selected date was used or not
     * @var {boolean}
     */
    private _defaultSelectedDateUsed: boolean = false;

    /**
     * Flag to check if the current date was used to get the avail dates
     * @var {boolean}
     */
    private _defaultCurrentDateRangeUsed: boolean = false;

    /**
     * Class constructor
     *
     * @param _analyticsHandler
     * @param _apiService
     * @param _configService
     * @param _translatePipe
     * @param _dateHandler
     * @param _analyticsConfig
     */
    constructor(
        private _analyticsHandler: AnalyticsHandler,
        private _apiService: ApiService,
        private _configService: ConfigService,
        private _translatePipe: TranslatePipe,
        private _dateHandler: DateHandler,
        private _analyticsConfig: AnalyticsConfigService
    ) {
        this._config = this._configService.getConfig();
    }

    /**
     * On init method
     */
    ngOnInit() {
        this._availabilityRestrictions = this._config.availabilityConfiguration;

        this._defaultDate = Utils.getValueFromQueryParam(Constants.PARAM_DEFAULT_DATE);

        this.reservationsTitle = 'reservations-available.text';
        this.reservationsDisclaimer = 'footer.disclaimer';

        this.parksList = this._config.parks;
        this._calendarDebounceTimeout = null;

        this._initializeDates();
        this._getAvailabilityTypes();

        this._apiService.selectedSegment.subscribe(
            (segment) => {
                if (!!segment) {
                    this.selectedSegment = segment;
                    this.selectedDate = {};
                    this.getAvailabilityForSelectedSegment();
                }
            }
        );

        this._analyticsConfig.autoPageViewTrackingTiming = Constants.ANALYTICS_PAGE_VIEW_TRACKING_TIME;
    };

    /**
     * Get availability data for the selected segment
     *
     * @returns {void}
     */
    public getAvailabilityForSelectedSegment(event?: any): void {
        const currentDate = this._dateHandler.convertDateToMomentObject(this._today);

        this._setCurrentDateRange(event ? event.detail.currentDate : '');
        
        //clear error message prior to make another call.
        this.errorMessage = null;

        /**
         * Small 500ms debounce timer to avoid unnecesary requests
         * if guests click the prev/next calendar button multiple times in a row
         */
        clearTimeout(this._calendarDebounceTimeout);

        this._calendarDebounceTimeout = setTimeout(() => {
            this._apiService
                .setRequestHeaders()
                .setRequestParams({
                    ...{'segment': this.selectedSegment},
                    ...this.currentDateRange
                })
                .getAvailability().subscribe(
                (response: any) => {
                    this.availableDates = response;

                    if (this.availableDates.length === 1 &&
                        Object.keys(this.availableDates[0]).length === 0) {
                            this.calendarHasContent = false;
                    } else {
                        this.calendarHasContent = true;
                    }

                    this._setSelectedDate(
                      !!this.availableDates.length ? this.availableDates[0].date : currentDate
                    );

                    if (!this._pageLoadAnalyticsTriggered) {
                        setTimeout(() => {
                            this._trackPageViewEvent(this.availableDates);
                        });
                    }

                    // If this was triggered because month changed, send data to analytics framework
                    if (!!event) {
                        this._sendDataToAnalytics(
                            Constants[`ANALYTICS_LINK_ID_MONTH_${event.detail.direction.toUpperCase()}`],
                            Constants[`ANALYTICS_EVENT_TYPE_MONTH_${event.detail.direction.toUpperCase()}`],
                            this.availableDates
                        );
                    }
                },
                (error: Error) => {
                    this.availableDates = [];
                    this.calendarHasContent = false;
                    this.errorMessage = 'error.generic';
                }
            );
        }, 500);
    }

    /**
     * Generate the availability types to show below the calendar
     *
     * @private
     * @returns {void}
     */
    private _getAvailabilityTypes() {
        const availabilityTypes = this._config.availabilityTypes;

        for (const index in availabilityTypes) {
            const rawAvailabilityType = availabilityTypes[index];
            const text = this._translatePipe.transform(rawAvailabilityType.text);

            this.availabilityTypes[index] = {
                ...rawAvailabilityType,
                ...{'text': text}
            };
        }
    }

    /**
     * Searchs the default date on the available dates
     *
     * @param availableDates
     * @param defaultDate
     * @private
     * @returns {boolean}
     */
    private _searchDefaultDate(availableDates, defaultDate){
        for (var i=0; i < availableDates.length; i++) {
            if (availableDates[i].date === defaultDate) {
                return true;
            }
        }
        
        return false;
    }

    /**
     * Sets the selected date information
     *
     * @param date
     * @private
     * @returns {void}
     */
    private _setSelectedDate(date: any): void {
        const momentDefaultDate = this._dateHandler.convertDateToMomentObject(this._defaultDate);
        const momentTodayDate = this._dateHandler.convertDateToMomentObject(this._today);
        const diff = momentDefaultDate.diff(momentTodayDate, 'days');

        if (
            !this._defaultSelectedDateUsed &&
            momentDefaultDate.isValid() &&
            this._defaultDate >= date &&
            diff <= Constants.AVAILABILITY_RESTRICTION_DEFAULT_RANGE &&
            this._searchDefaultDate(this.availableDates, this._defaultDate)
        ) {
            date = this._defaultDate;
            localStorage.setItem('selectedDate', date);
        } else {
            localStorage.removeItem('selectedDate');
        }

        this._defaultSelectedDateUsed = true;

        date = this._dateHandler.convertDateToMomentObject(date);

        this.selectedDate = {
            raw: this._dateHandler.formatDate(Constants.DATE_RAW, date),
            formatted: this._dateHandler.formatDate(Constants.DATE_PRETTY, date),
            availability: []
        };

        this.selectedDate.availability = this.availableDates.filter((availableDate: any) => {
            return availableDate.date === this.selectedDate.raw;
        })[0];
    }

    /**
     * Get detailed park availability for the selected date
     *
     * @param event Polymer event
     * @returns {void}
     */
    public getParksAvailability(event): void {
        // Shouldn't try to get availability if we don't have a date selected
        // or the calendar has no content
        if (Object.keys(this.selectedDate).length === 0 ||
            !this.calendarHasContent) return;

        let isAvailable = false,
            availableParks,
            analyticsLinkId = (!this._pageLoadAnalyticsTriggered)
                ? Constants.ANALYTICS_LINK_ID_INITIAL_AVAIL
                : Constants.ANALYTICS_LINK_ID_CHECK_AVAIL;

        this._setSelectedDate(event.detail.value);
        availableParks = this.selectedDate.availability.parks;
        this.selectedDate.availability = [];

        for (let park of this.parksList) {
            isAvailable = includes(availableParks, park.parkId);

            this.selectedDate.availability.push({
                parkId: park.parkId,
                parkName: this._translatePipe.transform(`park.${park.parkId}`),
                parkAnalyticsName: park.parkAnalyticsName,
                available: isAvailable,
                class: isAvailable ? Constants.PARK_AVAILABLE : Constants.PARK_UNAVAILABLE,
                availLabel: this._translatePipe.transform('park-available'),
                unavailLabel: this._translatePipe.transform('park-not-available')
            });
        }

        if (!this._pageLoadAnalyticsTriggered) {
            this._pageLoadAnalyticsTriggered = true;
        }

        this._sendDataToAnalytics(
            analyticsLinkId,
            Constants.ANALYTICS_EVENT_TYPE_DATE_CHANGED,
            this.selectedDate.availability
        );
    }

    /**
     * Initializes date strings
     *
     * @private
     * @returns {void}
     */
    private _initializeDates(): void {
        this._today = this._dateHandler.formatDate(Constants.DATE_RAW);
        this.calendarEndDate = this._getAvailabilityRestriction();
    }

    /**
     * Gets the availability restriction value
     *
     * @private
     * @returns {string}
     */
    private _getAvailabilityRestriction(): string {
        let availabilityRestrictionType = this._availabilityRestrictions.type;
        let availabilityRestrictionValue = this._availabilityRestrictions.value;
        let maxDate;

        if (!includes(values(Constants.AVAILABILITY_RESTRICTION_TYPES), availabilityRestrictionType)) {
            availabilityRestrictionType = Constants.AVAILABILITY_RESTRICTION_TYPES.range;
            availabilityRestrictionValue = Constants.AVAILABILITY_RESTRICTION_DEFAULT_RANGE;
        }

        switch (availabilityRestrictionType) {
            case Constants.AVAILABILITY_RESTRICTION_TYPES.specific:
                maxDate = this._dateHandler.convertDateToMomentObject(availabilityRestrictionValue || this._today);
                break;
            case Constants.AVAILABILITY_RESTRICTION_TYPES.range:
            default:
                maxDate = this._dateHandler.convertDateToMomentObject(this._today)
                    .add(availabilityRestrictionValue, 'days');
                break;
        }

        return this._dateHandler.formatDate(Constants.DATE_RAW, maxDate);
    }

    /**
     * Sets the date range to invoke the availability API
     *
     * @param date
     * @private
     *
     * @returns {void}
     */
    private _setCurrentDateRange(date?: any): void {
        const momentDefaultDate = this._dateHandler.convertDateToMomentObject(this._defaultDate);
        const momentTodayDate = this._dateHandler.convertDateToMomentObject(this._today);
        const diff = momentDefaultDate.diff(momentTodayDate, 'days');

        if (!date) {
            // Avoid overriding current date range if date is not set
            // so if we change segment, we'll get the availability
            // for the month the guest was seeing before
            if (Object.keys(this.currentDateRange).length !== 0) {
                return;
            }

            date = this._today;
        }

        if (
            !this._defaultCurrentDateRangeUsed &&
            momentDefaultDate.isValid() &&
            this._defaultDate >= this._today &&
            diff <= Constants.AVAILABILITY_RESTRICTION_DEFAULT_RANGE
        ) {
            date = this._defaultDate;
            localStorage.setItem('selectedDate', date);
        } else {
            localStorage.removeItem('selectedDate');
        }

        this._defaultCurrentDateRangeUsed = true;

        date = this._dateHandler.convertDateToMomentObject(date);

        this.currentDateRange = {
            'startDate': this._dateHandler.formatDate(
                Constants.DATE_RAW, this._getStartDate(date.startOf(Constants.DATE_UNIT_MONTH))
            ),
            'endDate': this._dateHandler.formatDate(
                Constants.DATE_RAW, this._getEndDate(date.endOf(Constants.DATE_UNIT_MONTH))
            )
        };
    }

    /**
     * Gets the start date for the current date range. If it's a past date, will return the current date.
     *
     * @param date
     * @private
     *
     * @returns {any}
     */
    private _getStartDate(date: any): any {
        return date.isBefore(this._today) ? this._dateHandler.convertDateToMomentObject(this._today) : date;
    }

    /**
     * Gets the end date for the current date range. If it's after calendar end date, will return that one instead
     *
     * @param date
     * @private
     *
     * @returns {any}
     */
    private _getEndDate(date: any): any {
        return (Utils.isIe())
          ? this.calendarEndDate
          : date.isAfter(this.calendarEndDate) ? this.calendarEndDate : date;
    }

    /**
     * Sends data to analytics framework
     *
     * @param linkId
     * @param eventType
     * @param parksAvailability
     * @private
     * @returns {void}
     */
    private _sendDataToAnalytics(linkId: string, eventType: string, parksAvailability: any): void {
        this._analyticsHandler
            .setLinkId(linkId)
            .setSelectedDate(this.selectedDate.raw)
            .setSelectedSegment(this.selectedSegment)
            .setParksAvailability(parksAvailability)
            .trackAnalytics(eventType);
    }

    /**
     * Tracks the page view event
     *
     * @param parksAvailability
     * @private
     * @returns {void}
     */
    private _trackPageViewEvent(parksAvailability: any): void {
        this._analyticsHandler
            .setSelectedDate(this.selectedDate.raw)
            .setSelectedSegment(this.selectedSegment)
            .setParksAvailability(parksAvailability)
            .trackPageViewEvent();
    }
}
